import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import ReadMoreText from '../components/ReadMoreText';
import { Calendar, User, ArrowLeft, Clock, Facebook, Linkedin } from 'lucide-react';
import api from '../utils/axios';
import getFullUrl from '../utils/formatUrl';

const BlogDetails = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [content, setContent] = useState({});
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showFullContent, setShowFullContent] = useState(false);
    const location = useLocation();

    // Get 'name' (slug) from URL ?name=slug
    const query = new URLSearchParams(location.search);
    const slug = query.get('name');
    const shareUrl = window.location.href;

    const shareActions = [
        { 
            icon: <Facebook size={18} />, 
            label: 'Facebook', 
            onClick: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank'),
            color: 'hover:bg-[#1877F2]' 
        },
        { 
            icon: (
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.445 0 .081 5.391.079 11.966c0 2.101.549 4.152 1.595 5.96L0 24l6.182-1.622a11.882 11.882 0 005.867 1.555h.005c6.604 0 11.967-5.391 11.97-11.967a11.893 11.893 0 00-3.492-8.472"/>
                </svg>
            ), 
            label: 'WhatsApp', 
            onClick: () => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent((blog?.title || '') + ' ' + shareUrl)}`, '_blank'),
            color: 'text-[#25D366] hover:bg-[#25D366]' 
        },
        { 
            icon: <Linkedin size={18} />, 
            label: 'LinkedIn', 
            onClick: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank'),
            color: 'hover:bg-[#0A66C2]' 
        }
    ];

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchData();
        window.scrollTo(0, 0);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchData = async () => {
        try {
            setLoading(true);
            
            // Fetch Web Content
            const resContent = await api.get('/content');
            const contentMap = {};
            resContent.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);

            // Fetch Blog Details
            if (slug) {
                const resBlog = await api.get(`/blogs/${slug}`);
                setBlog(resBlog.data.data);
            }
        } catch (err) {
            console.error("Failed to load blog details", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="w-12 h-12 border-4 border-[#ee3124] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!blog) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4">
                <h2 className="text-4xl font-black uppercase text-slate-900 mb-4">Post Not Found</h2>
                <Link to="/blog" className="text-[#ee3124] font-bold uppercase tracking-widest flex items-center">
                    <ArrowLeft className="mr-2" /> Back to Blog
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
            />

            {/* Article Hero */}
            <header className="relative h-[60vh] bg-slate-900 flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent z-10"></div>
                <img 
                    src={getFullUrl(blog.image)} 
                    className="absolute inset-0 w-full h-full object-cover"
                    alt={blog.title}
                />
                
                <div className="max-w-4xl mx-auto px-4 relative z-20 w-full pb-16">
                    <Link to="/blog" className="inline-flex items-center text-white/70 hover:text-[#ee3124] font-black uppercase text-[10px] tracking-widest mb-8 transition-colors">
                        <ArrowLeft size={16} className="mr-2" /> All Stories
                    </Link>
                    
                    <div className="flex items-center gap-4 mb-6">
                        <span className="bg-[#ee3124] text-white text-[10px] font-black uppercase py-1 px-4 tracking-widest shadow-lg">
                            {blog.category}
                        </span>
                        <div className="flex items-center text-[10px] font-bold text-white/80 tracking-widest uppercase">
                            <Calendar size={14} className="mr-2 text-[#ee3124]" />
                            {blog.date}
                        </div>
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter leading-none mb-4 italic">
                        {blog.title}
                    </h1>

                    <div className="flex items-center space-x-6 pt-4 border-t border-white/10 mt-6">
                        <div className="flex items-center text-white/70 font-bold uppercase text-[10px] tracking-widest">
                            <User size={14} className="mr-2 text-[#ee3124]" /> {blog.author}
                        </div>
                        <div className="flex items-center text-white/70 font-bold uppercase text-[10px] tracking-widest">
                            <Clock size={14} className="mr-2 text-[#ee3124]" /> 5 min read
                        </div>
                    </div>
                </div>
            </header>

            {/* Content Section */}
            <article className="py-20">
                <div className="max-w-4xl mx-auto px-4">
                    {/* Excerpt/Intro */}
                    <p className="text-2xl font-bold text-slate-800 leading-relaxed italic border-l-8 border-[#ee3124] pl-8 mb-12 py-4">
                        {blog.excerpt}
                    </p>

                    {/* Main Content */}
                    <ReadMoreText 
                        text={blog.content}
                        charLimit={800}
                        isHTML={true}
                        buttonClassName="text-[#ee3124] hover:text-[#212529] font-black uppercase text-sm tracking-widest"
                        containerClassName="prose prose-lg max-w-none prose-slate prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tighter prose-headings:italic prose-a:text-[#ee3124] prose-strong:text-slate-900 text-slate-600 leading-extra-relaxed text-lg"
                    />

                    {/* Footer Share */}
                    <div className="mt-20 pt-10 border-t border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center space-x-6">
                            <span className="text-[10px] font-black uppercase tracking-[4px] text-slate-400">Share Story:</span>
                            <div className="flex items-center gap-3">
                                {shareActions.map((action, idx) => (
                                    <button 
                                        key={idx} 
                                        onClick={action.onClick}
                                        title={`Share on ${action.label}`}
                                        className={`w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 ${action.color} hover:text-white transition-all shadow-sm transform hover:-translate-y-1`}
                                    >
                                        {action.icon}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <Link to="/blog" className="px-8 py-3 bg-[#212529] text-white font-black uppercase text-[10px] tracking-widest hover:bg-[#ee3124] transition-all shadow-lg text-center">
                            Return to Journal
                        </Link>
                    </div>
                </div>
            </article>

            {/* Related Posts? (Could add later) */}

            <Footer data={content} />
            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default BlogDetails;
